/**
 * useCases Hook
 * Fetches and manages medical cases from the backend API
 * Supports filtering, pagination, and sorting
 */

import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../Api';
import { API_ENDPOINTS } from '../constants/ApiEndpoints';

export const useCases = (options = {}) => {
  const {
    patientId = null,
    limit = 10,
    offset = 0,
    sortBy = 'date',
    sortOrder = 'desc',
    searchTerm = '',
  } = options;

  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [allCases, setAllCases] = useState(null); // cache for array responses
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Reset page and cache when filters change
  useEffect(() => {
    setCurrentPage(0);
    setAllCases(null);
  }, [patientId, searchTerm, sortBy, sortOrder]);

  const fetchCases = useCallback(async () => {
    // If we already have the full dataset cached, skip network call
    if (allCases) return;

    try {
      setIsLoading(true);
      setError(null);

      let endpoint;

      // Choose endpoint based on filters
      if (patientId) {
        endpoint = API_ENDPOINTS.CASE.GET_BY_PATIENT(patientId);
      } else {
        endpoint = API_ENDPOINTS.CASE.LIST;
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset + currentPage * limit);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
      }

      const url = `${endpoint}?${params.toString()}`;
      const data = await apiRequest(url);

      // Handle both array response and paginated response
      if (Array.isArray(data)) {
        // API returned all results — paginate client-side
        const pageStart = currentPage * limit;
        setCases(data.slice(pageStart, pageStart + limit));
        setTotal(data.length);
        setAllCases(data);
      } else if (data.items) {
        setCases(data.items);
        setTotal(data.total);
      } else {
        setCases([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError(err);
      setCases([]);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, limit, offset, sortBy, sortOrder, searchTerm, currentPage, allCases]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // When we have a full cached array, re-slice without a network call
  useEffect(() => {
    if (allCases) {
      const pageStart = currentPage * limit;
      setCases(allCases.slice(pageStart, pageStart + limit));
    }
  }, [currentPage, allCases, limit]);

  const nextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const previousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const goToPage = useCallback((page) => {
    setCurrentPage(Math.max(0, page));
  }, []);

  return {
    cases,
    total,
    isLoading,
    error,
    refetch: fetchCases,
    currentPage,
    pageSize: limit,
    totalPages: Math.ceil(total / limit),
    nextPage,
    previousPage,
    goToPage,
  };
};

export default useCases;
